import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import AdminPage from './pages/AdminPage';
import CustomerSearchPage from './pages/protocol/CustomerSearchPage';
import ProtocolFormPage from './pages/protocol/ProtocolFormPage';
import ProtocolSummaryPage from './pages/protocol/ProtocolSummaryPage';
import KlimaProtokollListPage from './pages/protocol/KlimaProtokollListPage';
import WPCustomerSearchPage from './pages/protocol/WPCustomerSearchPage';
import WaermepumpeForm from './pages/protocol/WaermepumpeForm';
import IBNKlimaCustomerSearchPage from './pages/protocol/IBNKlimaCustomerSearchPage';
import IBNKlimaForm from './pages/protocol/IBNKlimaForm';
import IBNWPCustomerSearchPage from './pages/protocol/IBNWPCustomerSearchPage';
import IBNWPForm from './pages/protocol/IBNWPForm';
import AngebotCustomerSearchPage from './pages/angebot/AngebotCustomerSearchPage';
import AngebotFormPage from './pages/angebot/AngebotFormPage';
import AngebotListPage from './pages/angebot/AngebotListPage';
import AngebotViewPage from './pages/angebot/AngebotViewPage';
import ProtocolListPage from './pages/ProtocolListPage';
import CustomerListPage from './pages/CustomerListPage';
import SettingsPage from './pages/SettingsPage';
import MessagesPage from './pages/MessagesPage';
import ChatPage from './pages/ChatPage';
import { CircularProgress, Box } from '@mui/material';

// Theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
}

// Public Route Component (redirects to dashboard if logged in)
function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return !isAuthenticated ? children : <Navigate to="/dashboard" />;
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/protocol/customer-search"
              element={
                <ProtectedRoute>
                  <CustomerSearchPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/protocol/form"
              element={
                <ProtectedRoute>
                  <ProtocolFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/protocol/summary"
              element={
                <ProtectedRoute>
                  <ProtocolSummaryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/protocol/klima-list"
              element={
                <ProtectedRoute>
                  <KlimaProtokollListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/protocol/wp-customer-search"
              element={
                <ProtectedRoute>
                  <WPCustomerSearchPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/protocol/wp-form"
              element={
                <ProtectedRoute>
                  <WaermepumpeForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/protocol/ibn-klima-customer-search"
              element={
                <ProtectedRoute>
                  <IBNKlimaCustomerSearchPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/protocol/ibn-klima-form"
              element={
                <ProtectedRoute>
                  <IBNKlimaForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/protocol/ibn-wp-customer-search"
              element={
                <ProtectedRoute>
                  <IBNWPCustomerSearchPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/protocol/ibn-wp-form"
              element={
                <ProtectedRoute>
                  <IBNWPForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/protocols"
              element={
                <ProtectedRoute>
                  <ProtocolListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customers"
              element={
                <ProtectedRoute>
                  <CustomerListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages"
              element={
                <ProtectedRoute>
                  <MessagesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages/chat/:receiverId"
              element={
                <ProtectedRoute>
                  <ChatPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/angebot/customer-search"
              element={
                <ProtectedRoute>
                  <AngebotCustomerSearchPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/angebot/form"
              element={
                <ProtectedRoute>
                  <AngebotFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/angebot-list"
              element={
                <ProtectedRoute>
                  <AngebotListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/angebot/view/:id"
              element={
                <ProtectedRoute>
                  <AngebotViewPage />
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
